import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  tokenSelector,
  emailSelector,
  titleSelector,
  roleSelector,
} from './reducer';
import { withTheme } from 'styled-components';
import { reduxForm } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';
import TitleBox from './components/TitleBox';
import './uploadFile.css';
import { getUploadsList, putAudioFile } from './sagas';
import { getNotes } from '../NotesPage/actions';
import swal from 'sweetalert';
import NoNotes from '../NotePage/components/NoteBox';
import Wrapper from './components/Wrapper';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

class UploadFile extends React.Component {
  state = {
    title: '',
    loading: false,
    uploadList: [],
    notes: [],
    isComprehensionRequested: false,
    isSuperBillRequested: false,
    currentPage: 1,
    itemsPerPage: 10,
    isLoaded: false,
    searchQuery: '',
    ffmpeg: null,
    ffmpegLoaded: false,
  };

  componentDidMount() {
    this.loadFFmpeg();
    this.getInitialData();
    this.props.dispatch(getNotes());
  }

  loadFFmpeg = async () => {
    const ffmpeg = new FFmpeg();
    await ffmpeg.load();
    this.setState({ ffmpeg, ffmpegLoaded: true });
  };

  getInitialData = async () => {
    this.setState({ isLoaded: false });

    try {
      const response = await getUploadsList(
        '/recordings/get-record-list',
        this.props.token
      );

      let uploadList = [];
      const localPending = JSON.parse(
        localStorage.getItem('pendingUploads') || '[]'
      );

      if (response && response.status === 200) {
        uploadList = response.data.records || [];

        const updatedPending = localPending.filter((localItem) => {
          const match = uploadList.find(
            (item) => item.recording_title === localItem.recording_title
          );
          return !match || match.status !== 'transcribed';
        });

        localStorage.setItem('pendingUploads', JSON.stringify(updatedPending));

        const mergedList = [...updatedPending, ...uploadList];

        this.setState({ uploadList: mergedList, isLoaded: true });
      } else {
        this.setState({ uploadList: localPending, isLoaded: true });
      }
    } catch (error) {
      const localPending = JSON.parse(
        localStorage.getItem('pendingUploads') || '[]'
      );
      this.setState({ uploadList: localPending, isLoaded: true });
    }
  };

  convertToMP3 = async (file) => {
    const { ffmpeg } = this.state;

    if (!ffmpeg || !this.state.ffmpegLoaded) {
      throw new Error('FFmpeg not loaded');
    }

    await ffmpeg.writeFile('input', await fetchFile(file));

    await ffmpeg.exec([
      '-i',
      'input',
      '-c:a',
      'mp3',
      '-b:a',
      '192k',
      'output.mp3',
    ]);

    const data = await ffmpeg.readFile('output.mp3');

    await ffmpeg.deleteFile('input');
    await ffmpeg.deleteFile('output.mp3');

    return new Blob([data.buffer], { type: 'audio/mp3' });
  };

  handleFileUploadClick = async () => {
    const { title, isComprehensionRequested, isSuperBillRequested } =
      this.state;

    if (!title) {
      swal({
        title: 'Error',
        text: 'Please add a title before uploading an audio file.',
        icon: 'error',
      });
      return;
    }

    if (
      this.props.role === 'pro' &&
      !isComprehensionRequested &&
      !isSuperBillRequested
    ) {
      const willProceed = await swal({
        title: 'Confirmation',
        text: 'You have not selected Comprehension or Super Bill request. Do you want to proceed without them?',
        icon: 'warning',
        buttons: ['Cancel', 'OK'],
        dangerMode: false,
      });

      if (!willProceed) {
        return;
      }
    }

    this.fileInputRef.click();
  };

  onFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeMB = 20;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      swal({
        title: 'File Too Large',
        text: `Maximum file size is ${maxSizeMB}MB`,
        icon: 'error',
      });
      e.target.value = '';
      return;
    }
    const newUpload = {
      recording_title: this.state.title,
      updated_at: new Date().toISOString(),
      status: 'pending',
      note_id: null,
      note_title: null,
    };

    const existing = JSON.parse(localStorage.getItem('pendingUploads') || '[]');
    const updated = [newUpload, ...existing];
    localStorage.setItem('pendingUploads', JSON.stringify(updated));

    try {
      this.setState({ loading: true });

      const mp3File = await this.convertToMP3(file);

      const metadata = {
        title: this.state.title,
        fileType: 'audio/mp3',
        isSuperBillRequested: this.state.isSuperBillRequested ? 1 : 0,
        isComprehensionRequested: this.state.isComprehensionRequested ? 1 : 0,
      };

      const response = await putAudioFile(
        '/recordings/generate-upload-url',
        { metadata, file: mp3File },
        this.props.token
      );

      if (response && response.status === 200) {
        this.setState((prevState) => {
          const updatedList = [newUpload, ...prevState.uploadList];
          return {
            uploadList: updatedList,
            title: '',
            loading: false,
            isComprehensionRequested: false,
            isSuperBillRequested: false,
          };
        });

        this.props.change('edit', 'title', '');
        swal({
          title: 'Success',
          text: 'Your audio has been uploaded successfully.',
          icon: 'success',
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      this.setState((prevState) => {
        const updatedList = prevState.uploadList;
        return { uploadList: updatedList, loading: false };
      });

      swal({
        title: 'Error',
        text: 'An error occurred while uploading the audio file.',
        icon: 'error',
      });
    }
    e.target.value = '';
  };

  handleSearchChange = (e) => {
    this.setState({ searchQuery: e.target.value, currentPage: 1 });
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page });
  };

  renderPagination = (totalItems) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    return (
      <Pagination style={{ marginTop: '20px', justifyContent: 'center' }}>
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink
            previous
            onClick={() => this.handlePageChange(currentPage - 1)}
          />
        </PaginationItem>
        {[...Array(totalPages)].map((_, index) => (
          <PaginationItem active={index + 1 === currentPage} key={index}>
            <PaginationLink onClick={() => this.handlePageChange(index + 1)}>
              {index + 1}
            </PaginationLink>
          </PaginationItem>
        ))}
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink
            next
            onClick={() => this.handlePageChange(currentPage + 1)}
          />
        </PaginationItem>
      </Pagination>
    );
  };

  render() {
    const {
      title,
      loading,
      uploadList,
      currentPage,
      itemsPerPage,
      isLoaded,
      searchQuery,
      ffmpegLoaded,
    } = this.state;

    const filteredUploads = uploadList.filter((upload) =>
      upload.recording_title?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const sortedUploads = [...filteredUploads].sort((a, b) => {
      return new Date(b.updated_at) - new Date(a.updated_at);
    });

    const currentUploads = sortedUploads.slice(
      indexOfFirstItem,
      indexOfLastItem
    );

    return (
      <div style={{ minHeight: '90vh' }}>
        <h4 className="text-center">Upload audio file for transcription</h4>

        <TitleBox className="mb-3">
          <FormattedMessage id="UploadFile.note">
            {(msg) => (
              <input
                className="title-input"
                name="title"
                placeholder={msg}
                type="text"
                maxLength={100}
                value={title}
                onChange={(e) => this.setState({ title: e.target.value })}
                style={{ backgroundColor: 'whitesmoke' }}
              />
            )}
          </FormattedMessage>
        </TitleBox>
        <p style={{ fontStyle: 'italic' }}>Up to 100 characters allowed.</p>
        {this.props.role === 'pro' && (
          <div
            className="d-flex justify-content-center mb-3"
            style={{ marginTop: '10px' }}
          >
            <label className="mr-4">
              <input
                type="checkbox"
                checked={this.state.isComprehensionRequested}
                onChange={(e) =>
                  this.setState({ isComprehensionRequested: e.target.checked })
                }
                style={{ marginRight: '4px' }}
              />
              Request Comprehension
            </label>
            <label>
              <input
                type="checkbox"
                checked={this.state.isSuperBillRequested}
                onChange={(e) =>
                  this.setState({ isSuperBillRequested: e.target.checked })
                }
                style={{ marginRight: '4px' }}
              />
              Request Super Bill
            </label>
          </div>
        )}

        <div className="d-flex justify-content-center mb-3">
          <input
            id="real-file"
            type="file"
            accept="audio/*"
            disabled={loading || !ffmpegLoaded}
            onChange={this.onFileChange}
            style={{ display: 'none' }}
            ref={(ref) => (this.fileInputRef = ref)}
          />

          <button
            type="button"
            className="custom-file-upload m-auto"
            disabled={loading || !ffmpegLoaded}
            onClick={this.handleFileUploadClick}
          >
            {loading
              ? 'Please wait...'
              : !ffmpegLoaded
                ? 'Loading converter...'
                : 'Select Audio File'}
          </button>
        </div>

        <h5 className="text-center mb-5">
          Select an audio file to transcribe. Maximum file size is{' '}
          <strong>20MB</strong>.
        </h5>

        {!isLoaded ? (
          <div className="text-center my-5">Loading audio files...</div>
        ) : (
          <>
            <div
              className="mb-3"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'end',
              }}
            >
              <input
                type="text"
                className="form-control"
                placeholder="Search audio files by title..."
                value={searchQuery}
                onChange={this.handleSearchChange}
                style={{ backgroundColor: 'whitesmoke', padding: '8px' }}
              />
            </div>

            <Wrapper>
              <div className="" style={{ overflowX: 'auto', width: '100%' }}>
                <table className="table-wrapper">
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #ddd',
                          width: '50%',
                        }}
                      >
                        Title
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px',
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUploads.length !== 0 ? (
                      currentUploads.map((ele, i) => {
                        const isClickable = ele.note_id && ele.note_title;
                        const status = ele.status || 'unknown';
                        const badgeStyle = {
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.8rem',
                          color: '#fff',
                          backgroundColor:
                            status === 'transcribed'
                              ? '#28a745'
                              : status === 'pending'
                                ? '#ffc107'
                                : '#6c757d',
                          textTransform: 'capitalize',
                        };

                        // Ensure valid date
                        const uploadDate = new Date(ele.updated_at);
                        const isValidDate = !isNaN(uploadDate.getTime());

                        return (
                          <tr key={ele.tempId || ele.note_id || i}>
                            <td
                              onClick={() =>
                                isClickable
                                  ? this.props.history.push(
                                      `/view/${ele.note_id}`
                                    )
                                  : null
                              }
                              style={{
                                padding: '8px',
                                cursor: isClickable ? 'pointer' : 'default',
                                color: isClickable ? '#007bff' : '#212529',
                                textDecoration: isClickable
                                  ? 'underline'
                                  : 'none',
                              }}
                            >
                              {ele.recording_title
                                ? ele.recording_title.length > 50
                                  ? ele.recording_title.slice(0, 50) + '...'
                                  : ele.recording_title
                                : 'N/A'}
                            </td>
                            <td style={{ padding: '8px' }}>
                              {isValidDate
                                ? uploadDate.toLocaleString(undefined, {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Invalid Date'}
                            </td>
                            <td style={{ padding: '8px' }}>
                              <span style={badgeStyle}>{status}</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'center' }}>
                          {searchQuery ? (
                            <NoNotes>
                              <img
                                alt="no-templates"
                                src={require('../../assets/no-notes.png')}
                                style={{ width: '100px', height: '100px' }}
                              />
                              <h1>
                                <FormattedMessage id="AudioFilePage.noAudioFile.title" />
                              </h1>
                              <div>
                                <p className="mb-0">
                                  <FormattedMessage id="AudioFilePage.noAudioFile.desc1" />
                                </p>
                              </div>
                            </NoNotes>
                          ) : (
                            <NoNotes>
                              <img
                                alt="no-templates"
                                src={require('../../assets/no-notes.png')}
                                style={{ width: '100px', height: '100px' }}
                              />
                              <h1>
                                <FormattedMessage id="AudioFilePage.noAudioFile.title" />
                              </h1>
                              <div>
                                <p className="mb-0">
                                  <FormattedMessage id="AudioFilePage.noAudioFile.desc2" />
                                </p>
                              </div>
                            </NoNotes>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Wrapper>
          </>
        )}
        {filteredUploads.length > itemsPerPage &&
          this.renderPagination(filteredUploads.length)}
      </div>
    );
  }
}

UploadFile = reduxForm({ form: 'edit' })(UploadFile);
UploadFile = withTheme(UploadFile);
UploadFile = connect(
  createStructuredSelector({
    token: tokenSelector,
    email: emailSelector,
    title: titleSelector,
    role: roleSelector,
    notes: (state) => state.notes || [],
  }),
  { getNotes }
)(UploadFile);

export default UploadFile;
